import { POPOVER_Z_INDEX } from '@srm/ui/lib/zIndex';
import { KeyboardEvent, useRef, useState } from 'react';
import { ActionIcon, Popover } from '@mantine/core';
import {
    MdOutlineDescription,
    MdOutlineMenuBook,
    MdOutlineBarChart,
    MdOutlineDashboard,
    MdOutlineNotifications,
    MdOutlineAssignment,
    MdOutlineLink,
    MdOutlineLanguage,
    MdOutlineOpenInNew,
    MdOutlineInfo,
    MdOutlineContentCopy,
} from 'react-icons/md';
import Button from '@srm/ui/button';
import type { ISystemLink } from '@/utils/resolveSystemLink';

const icons = {
    document: MdOutlineDescription,
    dictionary: MdOutlineMenuBook,
    report: MdOutlineBarChart,
    dashboard: MdOutlineDashboard,
    notification: MdOutlineNotifications,
    tasks: MdOutlineAssignment,
    internal: MdOutlineLink,
    external: MdOutlineLanguage,
};

const NotificationLink = ({ link }: { link: ISystemLink }) => {
    const [opened, setOpened] = useState(false);
    const [copyState, setCopyState] = useState('');
    const trigger = useRef<HTMLButtonElement>(null);
    const Icon = icons[link.kind];
    const external = link.kind === 'external';
    const closeOnEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && opened) {
            event.stopPropagation();
            setOpened(false);
            trigger.current?.focus();
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link.href);
            setCopyState('Ссылка скопирована');
        } catch {
            setCopyState('Не удалось скопировать. Выделите адрес и скопируйте вручную.');
        }
    };

    return (
        <span className="notification-link" data-kind={link.kind}>
            <a
                className="notification-link__anchor"
                href={link.href}
                target={link.target}
                rel="noopener noreferrer"
                title={link.href}
                onClick={(event) => event.stopPropagation()}
            >
                <Icon className="notification-link__icon" aria-hidden="true" />
                <span className="notification-link__content">
                    <span className="notification-link__title">
                        {link.title}
                        {external && <MdOutlineOpenInNew className="notification-link__external" aria-hidden="true" />}
                    </span>
                    <span className="notification-link__meta">
                        {link.title !== link.label && <span>{link.label}</span>}
                        {link.detail !== link.title && <span>{link.detail}</span>}
                        {external && <span className="notification-link__sr-only">Откроется в новой вкладке</span>}
                    </span>
                </span>
            </a>
            <Popover
                closeOnEscape={false}
                opened={opened}
                onChange={setOpened}
                withinPortal
                position="bottom-end"
                width={340}
                zIndex={POPOVER_Z_INDEX}
                returnFocus
                trapFocus
            >
                <Popover.Target>
                    <ActionIcon
                        ref={trigger}
                        type="button"
                        variant="subtle"
                        color="gray"
                        size={28}
                        className="notification-link__info"
                        aria-label={`Сведения о ссылке: ${link.title}`}
                        title="Сведения о ссылке"
                        data-notification-overlay={opened ? '' : undefined}
                        onKeyDownCapture={closeOnEscape}
                        onClick={(event) => {
                            event.stopPropagation();
                            setCopyState('');
                            setOpened((value) => !value);
                        }}
                    >
                        <MdOutlineInfo size={16} />
                    </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown
                    className="notification-link__popover"
                    data-notification-overlay=""
                    // Клик внутри вложенного портала не должен закрывать дропдаун уведомления.
                    onMouseDown={(event) => event.stopPropagation()}
                    onTouchStart={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDownCapture={closeOnEscape}
                >
                    <span className="notification-link__popover-label">{external ? 'Внешний сайт' : link.label}</span>
                    <span className="notification-link__address">{link.href}</span>
                    <Button
                        variant="text"
                        size="sm"
                        startAdornment={<MdOutlineContentCopy size={16} />}
                        onClick={() => void copy()}
                    >
                        Скопировать ссылку
                    </Button>
                    {copyState && (
                        <span className="notification-link__copy-status" role="status">
                            {copyState}
                        </span>
                    )}
                </Popover.Dropdown>
            </Popover>
        </span>
    );
};

export default NotificationLink;
